import {useNavigate} from "react-router-dom";
import React, {createContext, ReactNode, useContext, useEffect, useRef, useState,} from "react";
import {useStateContext} from "../../contexts/ContextProvider";
import {ChevronDoubleLeftIcon, ChevronDoubleRightIcon} from "@heroicons/react/24/outline";
import {Tooltip} from "../dataDisplay/Tooltip";
import SidebarUserMenu from "./SidebarUserMenu";
import FacilitySwitcher from "./FacilitySwitcher";
import FacilitySwitcherModal from "./FacilitySwitcherModal";

export const SidebarContext = createContext<{
    expanded: boolean;
    setExpanded: (b: boolean) => void;
}>({
    expanded: true,
    setExpanded: () => {
    },
});

function classNames(...classes: any[]) {
    return classes.filter(Boolean).join(" ");
}

export default function Sidebar({children, bottomItems}: { children: ReactNode; bottomItems?: ReactNode }) {

    const {currentFacility} = useStateContext();

    // État pour le modal de switch facility
    const [facilitySwitcherModalOpen, setFacilitySwitcherModalOpen] = useState(false);

    // Initialiser l'état depuis localStorage
    const [expanded, setExpanded] = useState(() => {
        const saved = localStorage.getItem('sidebarExpanded');
        return saved === 'true';
    });

    // Fonction pour toggle le sidebar
    const toggleSidebar = () => {
        setExpanded(prev => {
            const newValue = !prev;
            localStorage.setItem('sidebarExpanded', String(newValue));
            return newValue;
        });
    };

    // Sauvegarder dans localStorage à chaque changement
    useEffect(() => {
        localStorage.setItem('sidebarExpanded', String(expanded));
    }, [expanded]);

    return (
        <aside
            className={`h-screen fixed hidden lg:block z-50 transition-all duration-300 ease-out group/sidebar
                ${expanded ? 'w-60' : 'w-[62px]'}`}
        >
            <nav className="h-screen w-full flex flex-col bg-[#FAFAFA] border-r border-gray-200">
                {/* Logo section - fixed */}
                <div className="relative z-20 flex justify-center items-center w-full h-20 flex-none">
                    {expanded ? (
                        /* Mode expansé : Logo + bouton à droite */
                        <div className="w-full px-4 flex items-center justify-between">
                            <img
                                src="/images/logo-peace-and-work.png"
                                alt="Peace&Work"
                                className="h-6 transition-opacity duration-300 ease-in-out"
                            />
                            <button
                                onClick={toggleSidebar}
                                className="text-gray-400 hover:text-gray-600 transition-colors duration-150 p-1 hover:bg-gray-100 rounded-md"
                                title="Réduire le menu"
                            >
                                <ChevronDoubleLeftIcon className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        /* Mode réduit : Icône + bouton superposé au hover */
                        <div className="relative flex items-center justify-center w-full">
                            {/* Monogramme aux couleurs de la charte P&W : le logo
                                complet ne tient pas dans la barre réduite, et
                                l'icône TriggerFlow laissait croire à un autre produit. */}
                            <span
                                aria-label="Peace&Work"
                                className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#006F68] text-[11px] font-bold tracking-tight text-white group-hover/sidebar:opacity-0 transition-opacity duration-200"
                            >
                                P&amp;W
                            </span>
                            <button
                                onClick={toggleSidebar}
                                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/sidebar:opacity-100 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-150 rounded-md mx-3 my-2"
                                title="Agrandir le menu"
                            >
                                <ChevronDoubleRightIcon className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Facility Switcher - affiché seulement pour les groupes */}
                <FacilitySwitcher
                    isCompactMode={!expanded}
                    onClick={() => setFacilitySwitcherModalOpen(true)}
                />

                {/* Menu section - scrollable */}
                <SidebarContext.Provider value={{expanded, setExpanded}}>
                    <div className="flex-1 overflow-y-auto px-2 pt-4 sidebar-scroll">
                        <ul className="flex flex-col">
                            {children}
                        </ul>
                    </div>
                </SidebarContext.Provider>

                {/* Bottom section - fixed */}
                <div className="flex-none pb-3">
                    {bottomItems && (
                        <SidebarContext.Provider value={{expanded, setExpanded}}>
                            <ul className="px-2">
                                {bottomItems}
                            </ul>
                        </SidebarContext.Provider>
                    )}
                    <div className="w-full border-t border-gray-200 mt-2 mb-3"></div>
                    <SidebarUserMenu
                        expanded={expanded}
                        facility={currentFacility}
                    />
                </div>
            </nav>

            {/* Modal de switch facility */}
            <FacilitySwitcherModal
                open={facilitySwitcherModalOpen}
                setOpen={setFacilitySwitcherModalOpen}
            />
        </aside>
    );
}

export function SidebarSection({label}: { label: string }) {
    const {expanded} = useContext(SidebarContext);

    if (!expanded) {
        return <div className="my-2 mx-2 border-t border-gray-200"></div>;
    }

    return (
        <div className="mt-4 mb-1 px-3">
            <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
                {label}
            </span>
        </div>
    );
}

export function SidebarItem({
                                icon,
                                text,
                                link,
                                active,
                                alert,
                                badgeCount,
                                onClick,
                            }: {
    icon: React.ReactNode;
    text: string;
    link?: string;
    active?: boolean;
    alert?: boolean;
    badgeCount?: number;
    onClick?: () => void;
}) {
    const {expanded} = useContext(SidebarContext);
    const navigate = useNavigate();

    const liRef = useRef<HTMLLIElement>(null);
    const [hovered, setHovered] = useState(false);

    const handleClick = () => {
        if (link) {
            navigate(link);
        }
        if (onClick) {
            onClick();
        }
    };

    // Formatage du badge (99+ si > 99)
    const formattedBadge = badgeCount !== undefined && badgeCount > 0
        ? (badgeCount > 99 ? '99+' : badgeCount.toString())
        : null;

    return (
        <>
            <li
                ref={liRef}
                onClick={handleClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                className={classNames(
                    "relative flex items-center py-[6px] px-3 rounded-lg cursor-pointer transition-all duration-150 ease-out group",
                    expanded ? "justify-start" : "justify-center",
                    active
                        ? "bg-main-color-light text-main-color"
                        : "text-gray-700 hover:bg-gray-100"
                )}
            >
                <span className={classNames(
                    "flex-shrink-0 transition-colors duration-150",
                    active ? "text-main-color" : "text-gray-500 group-hover:text-gray-600"
                )}>
                    {React.cloneElement(icon as React.ReactElement, {
                        className: "w-5 h-5",
                        strokeWidth: 1.6
                    })}
                </span>
                <span
                    className={classNames(
                        "overflow-hidden transition-all duration-200 whitespace-nowrap text-[14.5px]",
                        expanded ? "w-44 ml-3 opacity-100" : "w-0 opacity-0",
                        active ? "font-medium" : "font-normal"
                    )}
                >
                    {text}
                </span>
                {/* Badge numérique pour les notifications */}
                {formattedBadge && expanded && (
                    <span className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold text-white bg-red-500 rounded-full">
                        {formattedBadge}
                    </span>
                )}
                {formattedBadge && !expanded && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold text-white bg-red-500 rounded-full">
                        {formattedBadge}
                    </span>
                )}
                {/* Point rouge simple (rétrocompatibilité) */}
                {alert && !formattedBadge && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"/>
                )}
            </li>

            {!expanded && hovered && liRef.current && (
                <Tooltip targetRef={liRef}>
                    {text}
                    {formattedBadge && ` (${formattedBadge} non lu${badgeCount && badgeCount > 1 ? 's' : ''})`}
                </Tooltip>
            )}
        </>
    );
}
